import { useRef, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

interface Transcript {
  source: string;
  message: string;
  timestamp: Date;
}

interface TranscriptReviewProps {
  transcript: Transcript[];
  onPracticeAgain: () => void;
}

export const TranscriptReview = ({
  transcript,
  onPracticeAgain,
}: TranscriptReviewProps) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll to bottom when component mounts
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="flex flex-col h-full overflow-hidden max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center py-4 bg-white rounded-xl shadow-sm mb-4">
        <h2 className="text-xl lg:text-2xl font-semibold text-primary">
          {t('custom.roleplay.transcript.title')}
        </h2>
        <p className="text-sm text-gray-600 mt-2">
          {t('custom.roleplay.transcript.description')}
        </p>
      </div>

      {/* Transcript Messages */}
      <div className="flex-1 overflow-y-auto bg-white rounded-xl shadow-sm p-4 lg:p-6 mb-4">
        <div className="space-y-3">
          {transcript.map((item, index) => {
            // Handle system messages differently
            if (item.source === 'system') {
              return (
                <div key={index} className="text-center my-4">
                  <p className="text-xs text-gray-500 italic">{item.message}</p>
                </div>
              );
            }

            const isAgent = item.source === 'ai';

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`flex flex-col mb-3 ${
                  isAgent ? 'items-start' : 'items-end'
                }`}
              >
                <div
                  className={`inline-block max-w-[80%] px-4 py-3 rounded-xl shadow-sm ${
                    isAgent
                      ? 'bg-red-50 text-gray-800 rounded-tl-none'
                      : 'bg-blue-50 text-gray-800 rounded-tr-none ml-auto'
                  }`}
                >
                  <p className="text-sm">{item.message}</p>
                </div>
                <span className="text-xs text-gray-500 mt-1 px-2">
                  {item.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </motion.div>
            );
          })}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Practice Again Button */}
      <div className="bg-white rounded-xl shadow-sm p-6 text-center">
        <button
          onClick={onPracticeAgain}
          className="flex items-center justify-center space-x-2 px-6 py-3 rounded-lg shadow-md transition-all duration-200 bg-primary text-white font-medium min-w-[200px] hover:bg-primary-dark hover:shadow-lg active:transform active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 mx-auto"
        >
          <RefreshCw className="w-5 h-5" />
          <span>{t('custom.roleplay.buttons.practiceAgain')}</span>
        </button>
      </div>
    </div>
  );
};
